import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { JOB_TYPES, WORK_MODES } from "@/lib/constants";

const EMPTY = { title: "", description: "", type: "Full-time", work_mode: "On-site", city: "", salary: "", experience: "" };

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const PostJobDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", form);
      onCreated();
      toast.success("Job posted");
      setOpen(false);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="post-job-button"><Plus className="mr-1.5 h-4 w-4" />Post a job</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="post-job-dialog">
        <DialogHeader><DialogTitle className="font-display text-tm-green">Post a job</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input required value={form.title} onChange={set("title")} data-testid="job-title-input" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea required rows={4} value={form.description} onChange={set("description")} data-testid="job-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Type</Label><Pick value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={JOB_TYPES} testid="job-type-select" /></div>
            <div className="space-y-2"><Label>Work mode</Label><Pick value={form.work_mode} onChange={(v) => setForm({ ...form, work_mode: v })} options={WORK_MODES} testid="job-workmode-select" /></div>
            <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={set("city")} data-testid="job-city-input" /></div>
            <div className="space-y-2"><Label>Salary</Label><Input placeholder="₹25,000 / month" value={form.salary} onChange={set("salary")} data-testid="job-salary-input" /></div>
          </div>
          <div className="space-y-2"><Label>Experience required</Label><Input placeholder="2+ years" value={form.experience} onChange={set("experience")} data-testid="job-experience-input" /></div>
          <Button type="submit" className="w-full rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="job-submit-button">Publish job</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
